import "react-virtualized/styles.css";

import React from "react";
import { AutoSizer, Table as TableBase, TableProps } from "react-virtualized";
import { ClassNames } from "@emotion/core";
import Box from "../Box/Box";
import { getPrimaryStyles, getPlainStyles, getMinimalStyles } from "./styles";
import { SxStyleProp } from "theme-ui";

export interface Props {
  sx?: SxStyleProp;
  data: any[];
  width?: number;
  headerClassName?: string;
  height?: number;
  variant?: "plain" | "primary" | "minimal";
  sort?: TableProps["sort"];
  sortBy?: TableProps["sortBy"];
  sortDirection?: TableProps["sortDirection"];
  onRowsRendered?: TableProps["onRowsRendered"];
  noRowsRenderer?: TableProps["noRowsRenderer"];
  onRowClick?: TableProps["onRowClick"];
  rowRenderer?: TableProps["rowRenderer"];
  rowHeight?: TableProps["rowHeight"];
  headerRowRenderer?: TableProps["headerRowRenderer"];
  ref?: any;
  hasHover?: boolean;
}

const Base: React.FunctionComponent<Props> = React.forwardRef(
  (
    {
      sx,
      width,
      height,
      data,
      variant = "primary",
      hasHover,
      headerClassName,
      ...restProps
    },
    ref,
  ) => {
    const getTableStyles = ({ css, theme, hasHover }: any) => {
      switch (variant) {
        case "primary":
          return getPrimaryStyles({ css, theme, hasHover });
        case "plain":
          return getPlainStyles({ css, theme, hasHover });
        case "minimal":
          return getMinimalStyles({ css, theme, hasHover });
        default:
          return "primary";
      }
    };
    return (
      <Box sx={sx}>
        <ClassNames>
          {({ css, theme }) => (
            <TableBase
              ref={ref as any}
              width={width!}
              height={height!}
              headerHeight={45}
              headerClassName={headerClassName}
              rowHeight={45}
              rowCount={data.length}
              rowGetter={({ index }) => data[index]}
              rowClassName={({ index }) => {
                if (index < 0) {
                  return "odd";
                }
                return index % 2 === 0 ? "even" : "odd";
              }}
              className={getTableStyles({ css, theme, hasHover })}
              {...restProps}
            />
          )}
        </ClassNames>
      </Box>
    );
  },
);

const Table: React.FunctionComponent<Props> = React.forwardRef(
  ({ width = 0, height = 0, data, ...restProps }, ref) => {
    // If no width/height passed, use AutoSizer to get width/height
    if (!width || !height) {
      return (
        <AutoSizer>
          {({ width: autoWidth, height: autoHeight }) => (
            <Base
              ref={ref}
              width={autoWidth || width}
              height={autoHeight || height}
              data={data}
              {...restProps}
            />
          )}
        </AutoSizer>
      );
    }

    return (
      <Base
        ref={ref}
        width={width}
        height={height}
        data={data}
        {...restProps}
      />
    );
  },
);

export default Table;
